/*
 * <dwm-reviews> — turns a readable list of reviews into accessible tabs ("one voice at a time").
 *
 * Without this script every review stays visible as a list. With it:
 *   - the client names become a tablist (role=tablist/tab/tabpanel, roving tabindex);
 *   - click, ArrowLeft/Right/Up/Down, Home and End select a review;
 *   - one panel is visible and the section follows its height; only the content below the list of names moves,
 *     and only after the visitor picks a review;
 *   - the chosen name is scrolled into view inside the list (vertical on desktop, horizontal on mobile) without
 *     scrolling the page; soft edge fades show when there are more names before or after;
 *   - no autoplay. The crossfade is CSS only and switched off for reduced motion;
 *   - optional previous/next buttons ([data-reviews-pager]) browse the reviews in order, wrap around, keep focus on the
 *     button, update the counter and announce the new review (company, number) politely.
 * In the Theme Editor, selecting a review block shows that review.
 */
class DwmReviews extends HTMLElement {
  #nav = null;
  #tabs = [];
  #panels = [];
  #index = 0;
  #resizeObserver = null;
  #pager = null;
  #current = null;
  #live = null;

  connectedCallback() {
    this.#nav = this.querySelector('[data-reviews-nav]');
    this.#tabs = [...this.querySelectorAll('[data-reviews-tab]')];
    this.#panels = [...this.querySelectorAll('[data-reviews-panel]')];

    if (!this.#nav || this.#tabs.length < 2 || this.#tabs.length !== this.#panels.length) {
      return;
    }

    this.#panels.forEach((panel, i) => {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', this.#tabs[i].id);
      panel.setAttribute('tabindex', '0');
    });

    this.#nav.removeAttribute('hidden');

    this.#pager = this.querySelector('[data-reviews-pager]');
    if (this.#pager) {
      this.#current = this.#pager.querySelector('[data-reviews-current]');
      this.#live = this.#pager.querySelector('[data-reviews-live]');
      this.#pager.removeAttribute('hidden');
      this.#pager.addEventListener('click', this.#onPager);
    }
    this.classList.add('is-enhanced');
    this.#nav.addEventListener('click', this.#onClick);
    this.#nav.addEventListener('keydown', this.#onKeydown);
    this.#nav.addEventListener('scroll', this.#updateFades, { passive: true });
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    this.#resizeObserver = new ResizeObserver(this.#updateFades);
    this.#resizeObserver.observe(this.#nav);
    this.select(0, { smooth: false });
  }

  disconnectedCallback() {
    this.#nav?.removeEventListener('click', this.#onClick);
    this.#nav?.removeEventListener('keydown', this.#onKeydown);
    this.#nav?.removeEventListener('scroll', this.#updateFades);
    this.#pager?.removeEventListener('click', this.#onPager);
    this.#resizeObserver?.disconnect();
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  select(index, { focus = false, smooth = true } = {}) {
    const count = this.#tabs.length;
    this.#index = ((index % count) + count) % count;

    this.#tabs.forEach((tab, i) => {
      const active = i === this.#index;
      tab.setAttribute('aria-selected', String(active));
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.#panels.forEach((panel, i) => {
      const active = i === this.#index;
      panel.classList.toggle('is-active', active);
      panel.toggleAttribute('inert', !active);
    });

    const tab = this.#tabs[this.#index];

    if (this.#current) {
      this.#current.textContent = String(this.#index + 1).padStart(2, '0');
    }

    if (focus) {
      tab.focus({ preventScroll: true });
    }

    this.#reveal(tab, smooth);
  }

  // Keep the chosen name in view inside the list without ever scrolling the page. The nav is the offset parent.
  #reveal(tab, smooth) {
    const nav = this.#nav;
    const behavior = smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto';

    if (nav.scrollWidth > nav.clientWidth + 1) {
      // Mobile row: centre the chosen name.
      nav.scrollTo({ left: tab.offsetLeft - (nav.clientWidth - tab.offsetWidth) / 2, behavior });
    } else if (nav.scrollHeight > nav.clientHeight + 1) {
      // Desktop list: only scroll when the name is (partly) hidden behind a fade, so the list stays calm.
      const margin = 48;
      const top = tab.offsetTop - margin;
      const bottom = tab.offsetTop + tab.offsetHeight + margin - nav.clientHeight;

      if (top < nav.scrollTop) {
        nav.scrollTo({ top: Math.max(0, top), behavior });
      } else if (bottom > nav.scrollTop) {
        nav.scrollTo({ top: bottom, behavior });
      }
    }
  }

  #updateFades = () => {
    const nav = this.#nav;
    const horizontal = nav.scrollWidth > nav.clientWidth + 1;
    const position = horizontal ? nav.scrollLeft : nav.scrollTop;
    const max = horizontal ? nav.scrollWidth - nav.clientWidth : nav.scrollHeight - nav.clientHeight;

    nav.classList.toggle('has-more-before', position > 2);
    nav.classList.toggle('has-more-after', max - position > 2);
  };

  #onClick = (event) => {
    const tab = event.target.closest('[data-reviews-tab]');

    if (tab) {
      this.select(this.#tabs.indexOf(tab));
    }
  };

  #onPager = (event) => {
    const button = event.target.closest('[data-reviews-prev], [data-reviews-next]');

    if (!button) {
      return;
    }

    this.select(this.#index + (button.hasAttribute('data-reviews-next') ? 1 : -1));

    if (this.#live) {
      const company = this.#tabs[this.#index].querySelector('.dwm-reviews__tab-company')?.textContent.trim() || '';
      this.#live.textContent = `Review ${this.#index + 1} van ${this.#tabs.length}: ${company}`;
    }
  };

  #onKeydown = (event) => {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

    if (event.key in keys) {
      event.preventDefault();
      this.select(this.#index + keys[event.key], { focus: true });
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.select(event.key === 'Home' ? 0 : this.#tabs.length - 1, { focus: true });
    }
  };

  #onBlockSelect = (event) => {
    const index = this.#panels.findIndex((panel) => panel.contains(event.target) || panel === event.target);

    if (index >= 0) {
      this.select(index);
    }
  };
}

if (!customElements.get('dwm-reviews')) {
  customElements.define('dwm-reviews', DwmReviews);
}
